import React, { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { getProjectPid } from "../../utils/sessionProject";
import {
  ChevronLeft, ChevronRight, Zap, DollarSign,
  Activity, UserRound, CalendarDays,
} from "lucide-react";
import "../../assets/css/ProjectList.css";
import "../../assets/css/Urls.css";

const BASE = process.env.REACT_APP_BASE_URL;
const getAuthHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const FEATURE_META = {
  tts:                 { bg: "#eff6ff", color: "#1d4ed8" },
  voice:               { bg: "#f1f5f9", color: "#475569" },
  embed:               { bg: "#fff7ed", color: "#c2410c" },
  chat:                { bg: "#f0fdf4", color: "#166534" },
  create_project_docs: { bg: "#fdf4ff", color: "#7e22ce" },
};

const featureMeta = (f = "") => FEATURE_META[f.toLowerCase()] ?? { bg: "#f1f5f9", color: "#334155" };

const formatFeature = (f = "") =>
  f.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const formatDate = (iso) => {
  const d = new Date(iso);
  return {
    date: d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }),
    time: d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true }),
  };
};

const formatCost = (n) =>
  n === 0 ? "$0.000000" : `$${Number(n).toFixed(6)}`;

const getPaginationItems = (cur, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  if (cur <= 4)         return [1, 2, 3, 4, 5, "e1", total];
  if (cur >= total - 3) return [1, "e0", total - 4, total - 3, total - 2, total - 1, total];
  return [1, "e0", cur - 1, cur, cur + 1, "e1", total];
};

const StatCard = ({ icon: Icon, iconBg, iconColor, label, value, sub, leftAccent }) => (
  <div style={{
    background: "#fff",
    border: "1px solid var(--borderColor)",
    borderLeft: leftAccent ? `3px solid ${iconColor}` : undefined,
    borderRadius: 12,
    padding: "18px 20px",
    display: "flex", alignItems: "center", gap: 16,
    boxShadow: "0 1px 3px rgba(15,23,42,.05), 0 4px 12px rgba(15,23,42,.03)",
    width: "100%",
  }}>
    <div style={{
      width: 48, height: 48, borderRadius: 12,
      background: iconBg, color: iconColor,
      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
    }}>
      <Icon size={22} />
    </div>
    <div style={{ minWidth: 0, flex: 1 }}>
      <p style={{ margin: 0, fontSize: 12, color: "var(--muteColor)", fontWeight: 500, marginBottom: 2 }}>{label}</p>
      <h5 style={{
        margin: 0, fontWeight: 800, color: "#0f172a", lineHeight: 1.2, fontSize: 20,
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
      }}>{value}</h5>
      {sub && <p style={{ margin: "4px 0 0", fontSize: 11, color: "var(--muteColor)" }}>{sub}</p>}
    </div>
  </div>
);

const LIMIT = 20;

const UserUsageDetails = () => {
  const location   = useLocation();
  const user       = location.state?.user ?? null;
  const userPid    = user?.user_pid ?? user?.userPid ?? null;
  const projectPid = getProjectPid();

  const [items,      setItems]      = useState([]);
  const [total,      setTotal]      = useState(0);
  const [page,       setPage]       = useState(1);
  const [loading,    setLoading]    = useState(false);
  const [pageTokens, setPageTokens] = useState(0);
  const [pageCost,   setPageCost]   = useState(0);

  const fetchUsage = useCallback(async (pg) => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/api/v1/ai/project-usage/list`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeader() },
        body: JSON.stringify({ projectPid, ...(userPid ? { userPid } : {}), page: pg, limit: LIMIT }),
      });
      const data = await res.json();
      const list = data?.response?.items ?? [];
      setItems(list);
      setTotal(data?.response?.total ?? list.length);
      setPageTokens(list.reduce((s, i) => s + (i.totalTokens ?? 0), 0));
      setPageCost(list.reduce((s, i) => s + (i.totalCostUsd ?? 0), 0));
    } catch (err) {
      console.error("Failed to fetch user usage", err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [projectPid, userPid]);

  useEffect(() => { fetchUsage(page); }, [page, fetchUsage]);

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));
  const pageStart  = (page - 1) * LIMIT;
  const userName   = user
    ? `${user.first_name ?? user.firstName ?? ""} ${user.last_name ?? user.lastName ?? ""}`.trim()
    : null;

  return (
    <main className="app-wrapper url-list-page">
      <div className="container-fluid">

        {/* Stat Cards */}
        <div className="row g-3 mb-4">
          <div className="col-12 col-sm-6 col-xl-3">
            <StatCard
              icon={UserRound}
              iconBg="var(--primaryTint)" iconColor="var(--primaryColor)"
              label={userName ? "User" : "Project"}
              value={userName || "All Users"}
              sub={user?.email ?? "Project-wide usage"}
              leftAccent
            />
          </div>
          <div className="col-12 col-sm-6 col-xl-3">
            <StatCard
              icon={Activity}
              iconBg="#eff6ff" iconColor="#0369a1"
              label="Total Logs"
              value={total.toLocaleString()}
              sub="usage entries"
            />
          </div>
          <div className="col-12 col-sm-6 col-xl-3">
            <StatCard
              icon={Zap}
              iconBg="#fdf4ff" iconColor="#7e22ce"
              label="Tokens (page)"
              value={pageTokens.toLocaleString()}
              sub="this page"
            />
          </div>
          <div className="col-12 col-sm-6 col-xl-3">
            <StatCard
              icon={DollarSign}
              iconBg="#fff7ed" iconColor="var(--orangeColor)"
              label="Cost (page)"
              value={`$${pageCost.toFixed(6)}`}
              sub="this page"
            />
          </div>
        </div>

        {/* Table Card */}
        <section className="url-table-card">

          {/* Card Header */}
          <div style={{
            padding: "14px 20px", background: "#fafbfc",
            borderBottom: "1px solid var(--borderColor)",
            display: "flex", alignItems: "center", gap: 10,
          }}>
            <div style={{
              width: 34, height: 34, borderRadius: 9,
              background: "var(--primaryTint)", color: "var(--primaryColor)",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <Activity size={16} />
            </div>
            <div>
              <h6 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Usage Logs</h6>
              <p style={{ margin: 0, fontSize: 11, color: "var(--muteColor)" }}>
                {total} total entries{userName ? ` for ${userName}` : ""}
              </p>
            </div>
          </div>

          {/* Table */}
          <div style={{ overflowX: "auto" }}>
            <table className="table url-table table-hover align-middle mb-0" style={{ minWidth: 640 }}>
              <thead>
                <tr>
                  <th className="text-center" style={{ width: 48 }}>#</th>
                  <th style={{ width: "14%" }}>Feature</th>
                  <th>Model</th>
                  <th style={{ width: "18%" }}>
                    <span className="d-flex align-items-center gap-1"><CalendarDays size={12} /> Date</span>
                  </th>
                  <th style={{ width: "12%" }}>
                    <span className="d-flex align-items-center gap-1"><Zap size={12} /> Tokens</span>
                  </th>
                  <th style={{ width: "13%" }}>Cost</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" className="text-center text-muted py-5">Loading...</td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center text-muted py-5">No usage logs found.</td>
                  </tr>
                ) : (
                  items.map((item, idx) => {
                    const meta = featureMeta(item.feature);
                    const { date, time } = formatDate(item.createdAt);
                    return (
                      <tr key={item.usagePid}>
                        <td className="url-index text-center">{pageStart + idx + 1}</td>

                        <td>
                          <span style={{
                            display: "inline-block",
                            fontSize: 11, fontWeight: 700,
                            background: meta.bg, color: meta.color,
                            borderRadius: 5, padding: "3px 10px",
                          }}>
                            {item.feature ? formatFeature(item.feature) : "—"}
                          </span>
                        </td>

                        <td style={{ fontSize: 12, color: "#334155", fontWeight: 500 }}>
                          {item.modelName ?? "—"}
                        </td>

                        <td>
                          <div style={{ fontSize: 12, color: "#334155", fontWeight: 500 }}>{date}</div>
                          <div style={{ fontSize: 11, color: "var(--muteColor)" }}>{time}</div>
                        </td>

                        <td>
                          <span className="ph-token-badge">
                            <Zap size={10} />
                            {(item.totalTokens ?? 0).toLocaleString()}
                          </span>
                        </td>

                        <td style={{ fontSize: 12, fontWeight: 600, color: "#166634" }}>
                          {formatCost(item.totalCostUsd ?? 0)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {total > 0 && (
            <div className="url-pagination-wrap">
              <p className="url-pagination-summary mb-0">
                Showing {pageStart + 1}–{Math.min(pageStart + LIMIT, total)} of {total}
              </p>
              <nav>
                <ul className="url-pagination mb-0">
                  <li>
                    <button className="url-page-btn" disabled={page === 1}
                      onClick={() => setPage(p => p - 1)}>
                      <ChevronLeft size={15} />
                    </button>
                  </li>
                  {getPaginationItems(page, totalPages).map((item, i) => (
                    <li key={i}>
                      {typeof item === "string" ? (
                        <span className="url-page-ellipsis">…</span>
                      ) : (
                        <button
                          className={`url-page-btn${page === item ? " active" : ""}`}
                          onClick={() => setPage(item)}
                        >
                          {item}
                        </button>
                      )}
                    </li>
                  ))}
                  <li>
                    <button className="url-page-btn" disabled={page === totalPages}
                      onClick={() => setPage(p => p + 1)}>
                      <ChevronRight size={15} />
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </section>

      </div>
    </main>
  );
};

export default UserUsageDetails;
